import { commonUtils } from '@sato/components';
import { createClient, Entry } from 'contentful';
import { flatten } from 'lodash';

import { LanguageKey } from '@models/Language';

import { ContentType, EntryType, ParsedEntry } from '../types';
import { parseEntry } from '../utils/data.utils';

const _client = createClient({
  removeUnresolved: true,
  host: process.env.CONTENTFUL_HOST as string,
  space: process.env.CONTENTFUL_SPACE as string,
  accessToken: process.env.CONTENTFUL_SECRET as string,
  environment: process.env.CONTENTFUL_ENVIRONMENT,
});

type ContentfulQueryParams<T extends EntryType> = {
  contentType?: ContentType<T> | ContentType<T>[];
  include?: number;
  locale?: LanguageKey | '*';
  /** Size of batches that entries are fetched in from Contentful. Maximum value is `1000`*/
  batchSize?: number;
} & Record<string, unknown>;

/**
 * Fetches all entries of the given content type(s).
 * Can fetch large amounts of content by splitting data fetching into batches.
 */
export const getEntries = async <T extends EntryType>({
  contentType,
  include = 10,
  locale = 'fi',
  batchSize,
  ...rest
}: ContentfulQueryParams<T>): Promise<Entry<T['fields']>[]> => {
  const fetch = (skip: number) =>
    _client.getEntries<T['fields']>({
      include,
      content_type: contentType,
      skip,
      limit: batchSize,
      locale,
      ...rest,
    });

  const firstBatch = await fetch(0);

  // early exit if batch size was not provided or the first request was sufficient for obtaining all entries
  if (commonUtils.isNullable(batchSize) || firstBatch.total < batchSize)
    return firstBatch.items;

  // skip fetching the first page again
  const totalEntryCount = Math.ceil((firstBatch.total - batchSize) / batchSize);

  const restBatch = await Promise.all(
    [...Array(totalEntryCount).keys()].map((pageIndex) =>
      fetch((pageIndex + 1) * batchSize)
    )
  );

  return [...firstBatch.items, ...flatten(restBatch.map((x) => x.items))];
};

export const contentfulClient = {
  ..._client,
  getEntries,
  getParsedEntry: async <EntryT extends EntryType>(
    id: string,
    config?: ContentfulQueryParams<EntryT>
  ) => {
    const result = await _client.getEntry(id, config);

    if (!result) return null;

    return parseEntry(result as EntryT);
  },
  getParsedEntries: async <EntryT extends EntryType>(
    config: ContentfulQueryParams<EntryT>
  ) => {
    const result = await getEntries(config);

    return result.reduce<ParsedEntry<EntryT>[]>((acc, item) => {
      const entry = parseEntry(item as EntryT);

      if (commonUtils.isNullable(entry)) return acc;

      return [...acc, entry];
    }, []);
  },
};
